/* The 169 hand classes and the four actions: the vocabulary every other module speaks. */

/** The 13 ranks, high to low, in the order the grid and the notation parser use. */
export const RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"] as const;

export type Rank = (typeof RANKS)[number];

/** A hand class such as "AKs", "T9o" or "77": 169 in total. */
export type HandClass = `${Rank}${Rank}s` | `${Rank}${Rank}o` | `${Rank}${Rank}`;

/** Rank -> its place in RANKS, ace first. */
export const RI: Readonly<Record<string, number>> = Object.fromEntries(RANKS.map((r, i) => [r, i]));

export const SUITS = ["s", "h", "d", "c"] as const;
export type Suit = (typeof SUITS)[number];

/** Strongest first: the order actions are listed, filled and stacked in. */
export const ORDER = ["allin", "raise", "call", "fold"] as const;
export type Action = (typeof ORDER)[number];

/** The actions a chart writes down. Fold is whatever is left over. */
export const WRITTEN = ["allin", "raise", "call"] as const;
export type WrittenAction = (typeof WRITTEN)[number];

export const LABEL: Readonly<Record<Action, string>> = {
  allin: "All-In",
  raise: "Raise",
  call: "Call",
  fold: "Fold",
};

/** The grid as drawn: pairs on the diagonal, suited above it, offsuit below. */
export const HANDS: readonly (readonly HandClass[])[] = RANKS.map((hi, i) =>
  RANKS.map((lo, j): HandClass => (i === j ? `${hi}${lo}` : i < j ? `${hi}${lo}s` : `${lo}${hi}o`)),
);

/** The 169 hand classes in grid order, row by row. */
export const FLAT: readonly HandClass[] = HANDS.flat();

/** Hand -> its row in FLAT. */
export const HAND_AT: Readonly<Record<string, number>> = Object.fromEntries(
  FLAT.map((h, i) => [h, i]),
);

/** Number of combos a hand class covers: 4 suited, 12 offsuit, 6 for a pair. */
export function combosOf(hand: string): 4 | 6 | 12 {
  if (hand.length === 2) return 6;
  return hand.endsWith("s") ? 4 : 12;
}

export const r3 = (n: number): number => Math.round(n * 1000) / 1000;
export const r1 = (n: number): number => Math.round(n * 10) / 10;
